import { RulesQueryBuilder } from "../rules-query-builder";
import { QueryBuilder } from "../../../../../../../components/queries/query-builder";
import { QueryFilter, EntityProjectionPropertyModel, DataType } from "../../../../../../../../models/queries/queries.models";
import { LookupClientService } from "../../../../../../../../services/lookups/lookup-client.service";
import { CustomerService } from "../../../../../../../../services/modules/customer-client.service";
import { ManageRightShipUsersClientService } from "../../../../../../../../services/modules/righship-qi-manage-users.service";
import { LookupValueRequest, LookupValue } from "../../../../../../../../models/lookups/lookup.models";
import * as Enumerable from "linq";
import { LookupUtilities } from "../../../../../../../../utilities/lookup-utilities";
import { RightshipRuleConfigParameterModel, CustomerRuleConfigParameterModel, BusinessUnitRuleConfigParameterModel } from "../../../../../../../../models/customers/customer.models";
import { DateExpressionParser } from "../../../../../../../../configuration/date-expressions";
import * as moment from "moment";
import { ConfigService } from "@rightship-tech/common-ui/services/basic/config.service";

export abstract class BaseQueryBuilder<T> {
    protected filter: T;
    protected vm: QueryBuilder;
    protected shouldHidePropertyInEntity: boolean = false;
    protected shouldHideRuleParameter: boolean = false;
    protected hideProperty: boolean = false;
    protected rulesVM: RulesQueryBuilder;
    protected readonly: boolean = false;
    protected disabled: boolean = false;
    protected buildParameters: boolean = false;
    protected customerId: number;
    protected businessUnitId: number;
    rightshipRuleConfigParameters: RightshipRuleConfigParameterModel[] = [];
    customerRuleConfigParameters: CustomerRuleConfigParameterModel[] = [];
    businessUnitRuleConfigParameters: BusinessUnitRuleConfigParameterModel[] = [];

    constructor(protected lookupService: LookupClientService, protected customerService: CustomerService,
        protected manageRightShipUsersClientService: ManageRightShipUsersClientService, protected configService: ConfigService) { }

    async activate(param) {
        this.filter = param.filter;
        this.vm = param.vm;
        this.rulesVM = param.rulesVM;
        this.hideProperty = param.hideProperty;
        this.shouldHidePropertyInEntity = param.shouldHidePropertyInEntity;
        this.shouldHideRuleParameter = param.shouldHideRuleParameter;
        this.readonly = param.readonly;
        this.disabled = param.disabled;
        this.buildParameters = param.buildParameters;
        this.customerId = param.customerId;
        this.businessUnitId = param.businessUnitId;

        if (this.customerId && this.businessUnitId) {
            const cResponse = await this.customerService.getCustomerRuleParameters(this.customerId);
            const bResponse = await this.customerService.getBusinessUnitRuleParameters(this.businessUnitId);
            this.rightshipRuleConfigParameters = await this.manageRightShipUsersClientService.getRsRuleConfigParameters();
            this.customerRuleConfigParameters = cResponse ? cResponse.parameters : [];
            this.businessUnitRuleConfigParameters = bResponse ? bResponse.parameters : [];
        }
    }

    protected getSecondParameter(filter: QueryFilter, projectedProperty: EntityProjectionPropertyModel) {
        if (!projectedProperty) {
            return "";
        }
        const type = filter.filterProperty.type;
        return type == DataType.Date || type == DataType.DateTime ? projectedProperty.format : "";
    }

    isDisplayTextVisible: boolean;
    async displayText(filter: QueryFilter) {
        const ruleConfigParameterId = (<any>filter).filterRuleConfigParameterId;
        const b = this.businessUnitRuleConfigParameters.filter(a => a.ruleConfigParameterId == ruleConfigParameterId && !a.useDefault)[0];
        const c = this.customerRuleConfigParameters.filter(a => a.ruleConfigParameterId == ruleConfigParameterId && !a.useDefault)[0];
        const r = this.rightshipRuleConfigParameters.filter(a => a.ruleConfigParameterId == ruleConfigParameterId)[0];
        if (!b && !c && !r) {
            this.isDisplayTextVisible = false;
            return null;
        }

        const isAdvanced = b ? b.isAdvanced : c ? c.isAdvanced : r.isAdvanced;
        if (!isAdvanced) {
            this.isDisplayTextVisible = false;
            return null;
        }

        const advancedParameters = b ? b.ruleConfigAdvancedParameters.map(x => x.ruleConfigAdvancedParameterId) : c ? c.ruleConfigAdvancedParameters.map(x => x.ruleConfigAdvancedParameterId) : r.ruleConfigAdvancedParameters.map(x => x.ruleConfigAdvancedParameterId);
        const result = await this.rulesVM.rulesClientService.getAdvancedParameterDisplayText(this.rulesVM.vetId, ruleConfigParameterId, advancedParameters);
        this.isDisplayTextVisible = result != null && result != undefined;
        return result;
    }

    async getParameterValue(filter: QueryFilter) {
        const ruleConfigParameterId = (<any>filter).ruleConfigParameterId;
        const b = this.businessUnitRuleConfigParameters.filter(a => a.ruleConfigParameterId == ruleConfigParameterId && !a.useDefault)[0];
        const c = this.customerRuleConfigParameters.filter(a => a.ruleConfigParameterId == ruleConfigParameterId && !a.useDefault)[0];
        const r = this.rightshipRuleConfigParameters.filter(a => a.ruleConfigParameterId == ruleConfigParameterId)[0];

        if (!b && !c && !r)
            return "NULL";

        const isAdvanced = b ? b.isAdvanced : c ? c.isAdvanced : r.isAdvanced;
        if (isAdvanced) {
            const advancedParameters = b ? b.ruleConfigAdvancedParameters.map(x => x.ruleConfigAdvancedParameterId) : c ? c.ruleConfigAdvancedParameters.map(x => x.ruleConfigAdvancedParameterId) : r.ruleConfigAdvancedParameters.map(x => x.ruleConfigAdvancedParameterId);
            filter.filterValue = this.rulesVM.rulesClientService.getAdvancedParameterValue(this.rulesVM.vetId, ruleConfigParameterId, advancedParameters);
        } else {
            filter.filterValue = b ? b.value : c ? c.value : r.value;
        }
        const dataType: DataType = <DataType>filter.filterProperty.type;
        if (dataType == DataType.Lookup || dataType == DataType.GHGRatingSelector)
            filter.filterValue = JSON.parse(`[${filter.filterValue}]`);

        return filter.filterValue? filter.filterValue: "NULL";
    }

    protected async getDisplayValue(filter: QueryFilter, projectedProperty: EntityProjectionPropertyModel) {
        const dataType: DataType = <DataType>filter.filterProperty.type;
        if (dataType === DataType.Date || dataType === DataType.DateTime) {
            if (!DateExpressionParser.isDateExpression(filter.filterValue)) {
                return moment(moment.utc(filter.filterValue).toDate()).format(dataType === DataType.DateTime ? this.configService.config.common.defaultDateTimeFormat : this.configService.config.common.defaultDateFormat);
            }
        }
        else if (dataType === DataType.Boolean) {
            return filter.filterValue ? "Yes" : "No";
        } else if (dataType === DataType.Lookup || dataType === DataType.HierarchicalLookup) {
            const request = new LookupValueRequest();
            request.lookupValueQuery = filter.filterProperty.lookupMappedToProperty;
            request.parameters = this.getSecondParameter(filter, projectedProperty);
            const x = await this.lookupService.getLookup(request, "default");

            let value = null;
            if (dataType == DataType.Lookup) {
                if (Array.isArray(filter.filterValue)) {
                    const selected = Enumerable.from(x.allValuesIncludingInactive).where(x => filter.filterValue.indexOf(x.id) > -1).select(x => x.name);
                    value = selected.toJoinedString(", ");
                } else {
                    const ids = filter.filterValue ? filter.filterValue.toString().replace("[", "").replace("]", "").split(',') : [];
                    value = ids.map(id => {
                        if (!id) return "Not Selected";

                        const lookup = x.allValuesIncludingInactive.filter(z => z.id == id)[0]
                        return lookup ? lookup.name : "Not Selected";
                    }).join(', ');
                }
            } else {
                const selected = LookupUtilities.matchLookupByIdRecursively(<LookupValue[]>x.allValuesIncludingInactive, filter.filterValue);
                value = selected ? selected.name : null;
            }

            return value ? value : "";
        } else if (dataType === DataType.GHGRatingSelector) {
            let value = filter.filterValue;
            if (!Array.isArray(filter.filterValue)) {
                value = filter.filterValue ? filter.filterValue.toString().replace("[", "").replace("]", "").split(',') : [];
            }
            return value;
        }

        return filter.filterValue;
    }
}